import { useQuery } from '@tanstack/react-query';
import { useAppContext } from 'src/contexts/AppContext';
import { useClientLogContext } from 'src/contexts/ClientLogContext';
import { useUserHasFeature } from 'src/hooks/useUserHasFeature';
import { tryInvokeApi } from 'src/utils/tryExecuteUtils';
import {
  Feature,
  ReachProSupportCaseAttachmentResponse,
  ReachProSupportCaseClient,
} from 'src/WebApiController';

export const SUPPORT_CASE_ATTACHMENTS_QUERY_KEY =
  'reachpro-support-case-attachments';

export type AttachmentsByNote = Record<
  number,
  ReachProSupportCaseAttachmentResponse[]
>;

export function useReachProSupportCaseAttachments(
  workItemId: number | undefined
) {
  const { activeAccountWebClientConfig } = useAppContext();
  const { trackError } = useClientLogContext();
  const hasReachProSupportFeature = useUserHasFeature(Feature.ReachProSupport);

  const shouldQuery =
    hasReachProSupportFeature &&
    workItemId != null &&
    activeAccountWebClientConfig.activeAccountId != null;

  const query = useQuery({
    queryKey: [SUPPORT_CASE_ATTACHMENTS_QUERY_KEY, workItemId],
    queryFn: async (): Promise<AttachmentsByNote> => {
      if (!shouldQuery || workItemId == null) {
        return {};
      }

      const attachments = await tryInvokeApi(
        async () => {
          return await new ReachProSupportCaseClient(
            activeAccountWebClientConfig
          ).getSupportCaseAttachments(workItemId);
        },
        (error) => {
          trackError(
            'ReachProSupportCaseClient.getSupportCaseAttachments',
            error
          );
        }
      );

      if (!attachments) {
        return {};
      }

      // Group the flat list by noteId
      return attachments.reduce<AttachmentsByNote>((acc, attachment) => {
        const { noteId } = attachment;
        if (!acc[noteId]) {
          acc[noteId] = [];
        }
        acc[noteId].push(attachment);
        return acc;
      }, {});
    },
    enabled: shouldQuery,
    refetchInterval: shouldQuery ? 60000 : false,
    refetchOnWindowFocus: false,
  });

  return query;
}
